import moment from "moment"
import { omit, times } from "lodash"
import createModule from "../createModule"
import view from "./view"
import { getIsOpPro } from "modules/account-root/selectors"
import { getPortfolioFxHedgeEnabled } from "modules/portfolio-root/selectors/portfolios"
import { getCurrency } from "./selectors"
import { getData } from "./selectors/data"
import { getMetrics, getSelectedMetric } from "./selectors/metrics"
import { getItems } from "./selectors/items"
import { getDetailsItems, getDetailsData, getFactorsCategories, getTableDownloadData } from "./selectors/details"
import { getIsRfrAttributionEnabled } from "selectors/features"
import { getStatus } from "./selectors/status"
import { getPositionSet } from "helpers/comparison"
import { getResidualAttributionSummarySubquery, getResidualPeriodPerformanceSubquery } from "helpers/residualAttribution"
import { Creators } from "./model"
import { VIEW_TYPES } from "helpers/base"
import { getSelectedComparisonType } from "modules/comparison/selectors"
import { getFileName } from "modules/comparison/selectors/"
import { MODEL_METADATA_QUERY } from "resources/models"

export default createModule({
  view,
  externalProps: (state, props) => ({
    currency: getCurrency(state, props),
    fxHedgeEnabled: getPortfolioFxHedgeEnabled(state, props),
    isRfrAttributionEnabled: getIsRfrAttributionEnabled(state, props),
  }),
  required: ["account", "modelId", "positionSets", "startDate", "endDate"],
  data: [...times(5, (n) => {
    return {
      resource: `comparePerformancePositionSet${n}`,
      as: null,
      passthrough: n > 0,
      url: ({ modelId, positionSets, startDate, endDate, base, viewType, modelEarliestDate, currency, fxHedgeEnabled, isRfrAttributionEnabled }) => {
        if (!positionSets[n] || positionSets[n].uploading) {
          return null
        }

        // Start the position set in the past to make sure the date is available on the previous modeled date
        // We go one week in the past so we don't have to query the previous date
        let perfStartDate = moment(startDate).add(-1, "week").format("YYYY-MM-DD")
        if (perfStartDate < modelEarliestDate) {
          perfStartDate = modelEarliestDate
        }

        const positionSet = getPositionSet(positionSets, n, perfStartDate, { currency })

        // If a segment is applied on the positionSet, use that segment on the base
        const baseCopy = { ...base }
        if (positionSet.segment) {
          baseCopy.segment = positionSet.segment
        }

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${startDate}${endDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(baseCopy) : ""}${currency}${fxHedgeEnabled}${isRfrAttributionEnabled}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
                model (id: $modelId) {
                  simulation (positionSet: $positionSet, base: $base, from: $from, to: $to, interval: AUTO) {
                    performance {
                      date
                      totalReturn {
                        cumulativePercent
                      }
                      attribution {
                        summary {
                          factors {
                            cumulativePercent
                          }
                          specific {
                            cumulativePercent
                          }
                          trading {
                            cumulativePercent
                          }
                          ${isRfrAttributionEnabled ? getResidualAttributionSummarySubquery() : ""}
                        }
                        factors {
                          id
                          category
                          name
                          return {
                            cumulativePercent
                          }
                        }
                      }
                    },
                  }
                }
              }`,
          variables: {
            modelId,
            positionSet,
            from: startDate,
            to: endDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [baseCopy] : undefined,
          },
        }
      },
    }
  }), ...times(5, (n) => {
    return {
      resource: `comparePerformanceMetrics${n}`,
      as: null,
      passthrough: true,
      url: ({ modelId, positionSets, startDate, selectedDate, base, viewType, modelEarliestDate, currency, fxHedgeEnabled, isRfrAttributionEnabled }) => {
        if (!positionSets[n] || positionSets[n].uploading) {
          return null
        }

        // Start the position set in the past to make sure the date is available on the previous modeled date
        // We go one week in the past so we don't have to query the previous date
        let perfStartDate = moment(startDate).add(-1, "week").format("YYYY-MM-DD")
        if (perfStartDate < modelEarliestDate) {
          perfStartDate = modelEarliestDate
        }

        const positionSet = getPositionSet(positionSets, n, perfStartDate, { currency })
        const source = omit(getPositionSet(positionSets, 0, perfStartDate, { currency }), ["fxHedgeEnabled"])

        // If a segment is applied on the positionSet, use that segment on the base
        const baseCopy = { ...base }
        if (positionSet.segment) {
          baseCopy.segment = positionSet.segment
        }

        return {
          id: `${modelId}${JSON.stringify(positionSet)}${JSON.stringify(source)}${startDate}${selectedDate}${viewType === VIEW_TYPES.ACTIVE ? JSON.stringify(baseCopy) : ""}${currency}${fxHedgeEnabled}${isRfrAttributionEnabled}`,
          query: `query ($modelId: String!, $positionSet: PositionSetInput!, $source: PositionSetInput!, $base: [PositionSetInput], $from: Date!, $to: Date!) {
            model (id: $modelId) {
              simulation (positionSet: $positionSet, base: $base, from: $from, to: $to) {
                periodPerformance {
                  maxDrawdown {
                    drawdown
                  }
                  sortinoRatio
                  annualizedReturns
                  annualizedVolatility
                  informationRatio
                  attribution {
                    summary {
                      factors {
                        annualizedReturns
                        annualizedVolatility
                        informationRatio
                      }
                      specific {
                        annualizedReturns
                        annualizedVolatility
                        informationRatio
                      }
                      ${isRfrAttributionEnabled ? getResidualPeriodPerformanceSubquery() : ""}
                    }
                    factors {
                        id
                        annualizedReturns
                        annualizedVolatility
                        informationRatio
                    }
                  }
                }
                correlation(with: $source)
              }
            }
          }`,
          variables: {
            modelId,
            positionSet,
            source,
            from: startDate,
            to: selectedDate,
            base: viewType === VIEW_TYPES.ACTIVE ? [baseCopy] : undefined,
          },
        }
      },
    }
  }),
  MODEL_METADATA_QUERY,
  ],
  mapStateToProps: (state, props) => ({
    data: getData(state, props),
    metrics: getMetrics(state),
    items: getItems(state, props),
    account: state.global.selected.account,
    portfolioId: state.global.selected.portfolio,
    detailsItems: getDetailsItems(state, props),
    detailsData: getDetailsData(state, props),
    status: getStatus(state),
    selectedMetric: getSelectedMetric(state, props),
    isOpPro: getIsOpPro(state),
    factorCategories: getFactorsCategories(state, props),
    selectedComparisonType: getSelectedComparisonType(state),
    fileName: getFileName(state, props),
    tableDownloadData: getTableDownloadData(state, props),
  }),
  actions: {
    ...Creators,
  },
})
